export type AiTipCategory = 'general' | 'llm-workflows' | 'agentic-workflows';

export interface AiTip {
  id: string;
  category: AiTipCategory;
  title: string;
  body: string;
  cost: number;
}

export interface AiTipUnlockState {
  gems: number;
  unlockedAiTipIds: string[];
}

export interface AiTipPurchaseResult {
  state: AiTipUnlockState;
  purchased: boolean;
  reason?: 'already-unlocked' | 'not-found' | 'insufficient-gems';
}

export const aiTipCategoryLabels: Record<AiTipCategory, string> = {
  general: 'General',
  'llm-workflows': 'LLM workflows',
  'agentic-workflows': 'Agentic workflows',
};

export const aiTips: AiTip[] = [
  {
    id: 'fresh-thread',
    category: 'general',
    title: 'Fresh thread bonus',
    body: 'Start a new thread for each task. This helps keep the context clean and avoids the AI getting confused.',
    cost: 35,
  },
  {
    id: 'single-file-llm',
    category: 'llm-workflows',
    title: 'Tight prompt payload',
    body: 'Give all the code needed, and no more. Unlike agents, LLM chats will not build code or run tests. So keep things limited, and where possible, work within a single file.',
    cost: 55,
  },
  {
    id: 'prompt-priming-stack',
    category: 'llm-workflows',
    title: 'Prime before prompting',
    body: 'Of course you are getting an LLM to write your prompts, right? Do not ask for the prompt straight away: first get a feature description, then a technical implementation or architecture description. Once that context is primed, ask for the prompt.',
    cost: 85,
  },
  {
    id: 'agent-branch-safety',
    category: 'agentic-workflows',
    title: 'Branch shield protocol',
    body: 'Never let an agent push code to main. All agentic work should be done in its own branch, and merging a branch to main/dev should only ever be possible via an explicit human interaction.',
    cost: 100,
  },
  {
    id: 'agent-test-everything',
    category: 'agentic-workflows',
    title: 'Test gauntlet',
    body: 'Unit test everything. Put it in your system prompt, put it in AGENTS.md — every change must have proper tests and the tests must pass.',
    cost: 120,
  },
  {
    id: 'agent-sized-planning',
    category: 'agentic-workflows',
    title: 'Plan for the agent',
    body: 'If you are using an LLM to plan work that AI will carry out, say so early. Ask it to scope each phase so an agent can complete it in one shot.',
    cost: 135,
  },
  {
    id: 'tangible-planning-progress',
    category: 'agentic-workflows',
    title: 'Make every phase count',
    body: 'During planning, explicitly require every change, phase, or update to be a noticeable, tangible step forward.',
    cost: 145,
  },
  {
    id: 'generate-required-assets',
    category: 'agentic-workflows',
    title: 'Generate assets or track them',
    body: "I don't know if you are able to generate [images/videos/audio/spreadsheets/whatever] yourself? But if so, please make use of that skill when appropriate — i.e. if new assets are needed, going forwards, please generate them. If you are not able to generate suitable assets yourself, that is okay — in that case, whenever assets are needed, please keep track of that in a file called `required-assets.md`, so that I can supply whatever is needed for you myself. Thanks!",
    cost: 155,
  },
];

export function isAiTipUnlocked(state: AiTipUnlockState, tipId: string): boolean {
  return state.unlockedAiTipIds.includes(tipId);
}

export function purchaseAiTip(state: AiTipUnlockState, tipId: string): AiTipPurchaseResult {
  const tip = aiTips.find((candidate) => candidate.id === tipId);

  if (!tip) {
    return { state, purchased: false, reason: 'not-found' };
  }

  if (isAiTipUnlocked(state, tip.id)) {
    return { state, purchased: false, reason: 'already-unlocked' };
  }

  if (state.gems < tip.cost) {
    return { state, purchased: false, reason: 'insufficient-gems' };
  }

  return {
    state: {
      ...state,
      gems: state.gems - tip.cost,
      unlockedAiTipIds: [...state.unlockedAiTipIds, tip.id],
    },
    purchased: true,
  };
}
